/**
 * Pure helpers for chat, crews, emotes and invite hotkeys (no DOM, no engine; see socialFormat.test.ts).
 */
import { CREW_INVITE_TTL_MS, CREW_MAX, INVITE_TTL_MS, emoteName, type EmoteName } from '../../shared/gangs';
import type { ChatChannel } from '../../shared/messages/social';

// ----------------------------------------------------------------------------- chat

export const CHANNELS: readonly ChatChannel[] = ['say', 'gang', 'crew'];

export const CHANNEL_LABEL: Record<ChatChannel, string> = { say: 'Say', gang: 'Gang', crew: 'Crew' };
export const CHANNEL_HINT: Record<ChatChannel, string> = {
  say: 'Heard by anyone within about 45 m',
  gang: 'Only your gang',
  crew: 'Only your crew',
};

const PREFIXES: Record<string, ChatChannel> = { s: 'say', say: 'say', g: 'gang', gang: 'gang', c: 'crew', crew: 'crew' };

export interface ParsedChat {
  ch: ChatChannel;
  /** Trimmed text to send ('' = nothing to send, e.g. only a channel switch). */
  text: string;
  /** A `/g`-style prefix picked the channel. */
  switched: boolean;
}

/** `/g hello` -> gang, "hello". Unknown slash words are plain text (the server cleans it either way). */
export function parseChatInput(raw: string, current: ChatChannel): ParsedChat {
  const t = raw.trim();
  const m = /^\/([a-z]+)(?:\s+([\s\S]*))?$/i.exec(t);
  const ch = m ? PREFIXES[m[1]!.toLowerCase()] : undefined;
  if (m && ch) return { ch, text: (m[2] ?? '').trim(), switched: true };
  return { ch: current, text: t, switched: false };
}

export function availableChannels(o: { hasGang: boolean; hasCrew: boolean }): ChatChannel[] {
  return CHANNELS.filter((c) => c === 'say' || (c === 'gang' && o.hasGang) || (c === 'crew' && o.hasCrew));
}

/** Next channel for Tab; falls back to 'say' when the current one is unavailable. */
export function nextChannel(current: ChatChannel, avail: readonly ChatChannel[]): ChatChannel {
  if (avail.length === 0) return 'say';
  const i = avail.indexOf(current);
  return avail[(i + 1) % avail.length] ?? 'say';
}

/** A channel you can no longer use (left the gang) drops back to 'say'. */
export function usableChannel(current: ChatChannel, avail: readonly ChatChannel[]): ChatChannel {
  return avail.includes(current) ? current : 'say';
}

export const CHAT_FULL_MS = 12_000;
export const CHAT_GONE_MS = 18_000;

/** Opacity of a chat line `age` ms after it arrived: solid, then a slow fade. */
export function chatFade(ageMs: number): number {
  if (ageMs <= CHAT_FULL_MS) return 1;
  if (ageMs >= CHAT_GONE_MS) return 0;
  return 1 - (ageMs - CHAT_FULL_MS) / (CHAT_GONE_MS - CHAT_FULL_MS);
}

// ----------------------------------------------------------------------------- crews

export const CREW_BLURB = `A crew is up to ${CREW_MAX} of you for the evening, no gang needed: crew chat, your mates on the map, and Night Freight runs from the pier (two or more of you). A seat keeps for 12 hours if you drop out; the crew breaks up when fewer than two are left.`;

/** The book's line for somebody without a crew. */
export const CREW_PITCH = `Invite up to ${CREW_MAX - 1} others by name for an evening’s work: crew chat, your mates on the map, and Night Freight — the run that pays best, and only a crew can do it.`;

/**
 * What a crew does next, from how many of it are in the city now (you included). Night Freight is the
 * crew's job: it needs two at the pier and someone driving a car with a boot.
 */
export function crewNext(onlineCount: number, touch = false): string[] {
  if (onlineCount < 2) return ['Next: Night Freight needs two of you at the pier — wait for a mate, or invite somebody who is about.'];
  return [
    `Next: Night Freight. Meet at Bonded Warehouse No. 3 in Gullet Basin (the crate on the map), one of you driving a car with a boot — a van or a saloon takes four crates — and ${touch ? 'tap USE' : 'press E'} at the pier.`,
    'The crates cost £300 each up front and fetch £650 at the drop: about £1,400 profit a car-load, split between you.',
  ];
}

export interface NearbyCandidate {
  name: string;
  x: number;
  z: number;
  lingering: boolean;
}

/** Closest players first, skipping people who already are in `exclude` (case-insensitive) and AWAY ones. */
export function nearbyNames(players: readonly NearbyCandidate[], me: { x: number; z: number }, o: { range: number; max: number; exclude?: readonly string[] }): { name: string; dist: number }[] {
  const skip = new Set((o.exclude ?? []).map((n) => n.toLowerCase()));
  return players
    .filter((p) => !p.lingering && !skip.has(p.name.toLowerCase()))
    .map((p) => ({ name: p.name, dist: Math.hypot(p.x - me.x, p.z - me.z) }))
    .filter((p) => p.dist <= o.range)
    .sort((a, b) => a.dist - b.dist)
    .slice(0, o.max);
}

// ----------------------------------------------------------------------------- emotes

export const EMOTE_TEXT: Record<EmoteName, string> = {
  wave: 'waves',
  point: 'points',
  cheer: 'cheers',
  shrug: 'shrugs',
  salute: 'salutes',
  facepalm: 'facepalms',
  beckon: 'beckons',
  sit: 'sits down',
};

/** "*waves*" for a snapshot emote id (1-based, 0 = none); null when nothing is playing. */
export function emoteBubble(id: number): string | null {
  const n = emoteName(id);
  return n ? `*${EMOTE_TEXT[n]}*` : null;
}

// ----------------------------------------------------------------------------- invites (Y / N)

export interface SeenInvite {
  id: number;
  /** When the client first saw it (client ms). */
  seenAt: number;
}

export type InviteChoice = { kind: 'gang'; id: number } | { kind: 'crew'; id: number };

/**
 * The invite Y / N act on: the newest live gang invite, else the live crew invite. "Live" = seen
 * less than the server's TTL ago (the server's own expiry clock is not ours, so we count from arrival).
 */
export function chooseInvite(gang: readonly SeenInvite[], crew: SeenInvite | null, now: number): InviteChoice | null {
  const g = gang.filter((i) => now - i.seenAt < INVITE_TTL_MS).sort((a, b) => b.seenAt - a.seenAt || b.id - a.id)[0];
  if (g) return { kind: 'gang', id: g.id };
  if (crew && now - crew.seenAt < CREW_INVITE_TTL_MS) return { kind: 'crew', id: crew.id };
  return null;
}

// ----------------------------------------------------------------------------- colours

/** Perceived brightness 0..1 of a 0xRRGGBB colour. */
export function luma(color: number): number {
  const r = (color >> 16) & 255;
  const g = (color >> 8) & 255;
  const b = color & 255;
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

/** A gang colour that can be read on the dark chat background: too dark falls back to mustard. */
export function readableOnDark(color: number | undefined, fallback = 0xdba73f): number {
  return color === undefined || luma(color) < 0.3 ? fallback : color;
}
